import {GraphQlField} from '@sail/data';
import {
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
  OpenapiPostV1CryptoInternalPayinSessionConfirmInputCurrencyEnum,
  OpenapiPostV1CryptoInternalPayinSessionUpdateInputWalletParams,
} from '@stripe-internal/data-v1/schema/types';
import type {UpdatePayinSessionMutationData} from 'src/api/graphql/useUpdatePayinSessionMutation';
import type {PayinSessionLazyQueryData} from 'src/api/graphql/usePayinSessionLazyQuery';
import type {SetupSessionLazyQueryData} from 'src/api/graphql/useSetupSessionLazyQuery';
import {Abi, TypedDataDomain, TypedDataParameter} from 'viem';
import {
  EXTENSION_WALLET_PROVIDERS,
  WALLET_PROVIDERS,
  type ExtensionWalletOption,
  type ExtensionWalletProvider,
  type WalletConnectOption,
  type ExchangeOption,
  type WalletOption,
  type WalletProvider,
} from '@stripe-internal/wallet-connect-wrapper';

export type SupportedNetwork =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
export type SupportedCurrency =
  OpenapiPostV1CryptoInternalPayinSessionConfirmInputCurrencyEnum;
export type UpdateWalletVerificationSessionParam =
  OpenapiPostV1CryptoInternalPayinSessionUpdateInputWalletParams;

export type SupportedAsset = GraphQlField<
  PayinSessionLazyQueryData,
  'v1CryptoInternalPayinSession',
  'supported_currencies'
>;

export type State = GraphQlField<
  PayinSessionLazyQueryData,
  'v1CryptoInternalPayinSession',
  'state'
>;

export type TSelectedCurrencyId = `${SupportedCurrency}.${SupportedNetwork}`;

export type SerializedError = {
  name: string;
  message: string;
  stack: string | undefined;
};

export {
  EXTENSION_WALLET_PROVIDERS,
  WALLET_PROVIDERS,
  type ExtensionWalletOption,
  type ExtensionWalletProvider,
  type WalletConnectOption,
  type WalletOption,
  type WalletProvider,
  type ExchangeOption,
};

export const EVM_NETWORKS: readonly SupportedNetwork[] = [
  'ethereum',
  'polygon',
  'base',
  'tempo',
] as const;

export type EvmReadContractData = {
  abi: Abi;
  address: `0x${string}`;
  functionName: string;
  args: (bigint | `0x${string}`)[];
};

export type EvmTypedData = {
  domain: TypedDataDomain;
  types: Record<string, TypedDataParameter[]>;
  primaryType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any;
};

export type SolanaTransactionData = {
  destinationAddress: string;
  destinationTokenAccountAddress: string | null;
  currencyContractAddress: string;
  // amount is in token minor units (USDC and USDP minor unit is 6)
  amount: bigint;
  memoMessage: string;
  latestBlockhash: string;
  walletAddress: string;
  currency: string;
};

export type Wallet = {
  address: string;
  network: SupportedNetwork;
  type: WalletProvider;
};

export type Currency = {
  selectedCurrency: SupportedCurrency;
  selectedNetwork: SupportedNetwork;
};

export type WalletSession =
  | GraphQlField<
      PayinSessionLazyQueryData,
      'v1CryptoInternalPayinSession',
      'wallet_sessions'
    >
  | GraphQlField<
      UpdatePayinSessionMutationData,
      'v1CryptoInternalPayinSessionUpdate',
      'wallet_sessions'
    >;

export type CryptoPayinSession =
  | GraphQlField<PayinSessionLazyQueryData, 'v1CryptoInternalPayinSession'>
  | GraphQlField<
      UpdatePayinSessionMutationData,
      'v1CryptoInternalPayinSessionUpdate'
    >;

export type CryptoSetupSession = GraphQlField<
  SetupSessionLazyQueryData,
  'v1CryptoInternalSetupSession'
>;

export type Screen =
  | 'checkout'
  | 'wallet_connect'
  | 'wallet_select'
  | 'currency'
  | 'fulfillment'
  | 'fulfillment_failed'
  | 'cancelled'
  | 'link_login'
  | 'link_signup'
  | 'link_otp'
  | 'link_name'
  | 'volume_limit_exceeded';

export type ToastConfig = {
  message: JSX.Element;
  handleCancel?: () => void;
};

export type LinkInput = {
  email: string;
  phoneNumber: string;
  otp: string;
  startedVerification: boolean;
  firstName: string;
  lastName: string;
};

export type PayButton = {
  isPending: boolean;
  isErrored: boolean;
};

export type UrlParams = {
  embedded: boolean;
  theme: 'dark' | 'light' | null;
  selectedToken: string | null;
  selectedWallet: string | null;
};
